import { Modal, Tag } from 'antd'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ActionArea from '../../components/ActionArea'
import List from '../../components/List'
import FloatingButton from '../../components/UI/FloatingButton'
import SelectInput from '../../components/UI/SelectInput'
import { useLoading } from '../../context/LoadingProvider/useLoading'
import { changeJackpotStatusById, getJackpotsList } from '../../services/jackpots'
import direction from '../../utils/filterDirectionOrder'
import { IJackPot } from './types'
import {
  EditOutlined,
} from '@ant-design/icons'
import { sendSystemLog, useAuth } from '../../context/AuthProvider/useAuth'
import Button from '../../components/UI/Button'

const jackpotsStatusFilters = {
  active: 'active',
  inactive: 'inactive',
  all: 'all'
}

function index() {
  const loading = useLoading()
  const history = useNavigate()
  const {canEdit} = useAuth()
  const [params, setParams] = useState({
    status: jackpotsStatusFilters.all,
    fieldOrder: '',
    directionOrder: '',
  })
  const log = {
    log_type: 'visited',
    log_object: 'bingo_game_jackpot_instances'
  }

  useEffect(()=> {
    sendSystemLog(log)
    onRefresh()
  }, [])

  const [jackpots, setJackpots] = useState<IJackPot[]>([])

  const onRefresh = async (order?: string, directionparam?: string) => {
    loading.addLoading()
    let requestParams = {
      status: params.status,
      fieldOrder: '',
      directionOrder: ''
    }
    if(order && directionparam) {
      requestParams = {
        ...requestParams,
        fieldOrder: order || '',
        directionOrder: directionparam === 'ascend' ? direction.asc : direction.desc,
      }
      setParams({
        ...params,
        fieldOrder: order || '',
        directionOrder: directionparam === 'ascend' ? direction.asc : direction.desc,
      })
    } 
    const response = await getJackpotsList(requestParams)
    loading.removeLoading()
    setJackpots(response)
  }

  const onChangeOrder = (order: {column: any, field: string, order: string| undefined}) => {
    if(order.column) return onRefresh(order.field, order.order)
    return onRefresh()
  }

  const parseTemplateId = (template: string) => {
    return template.split('-')[0] || ''
  }

  const changeJackpotStatus = (id: string, status: string) => {
    if(status.toUpperCase() !== 'ACTIVE') return changeStatus({id, status: 'Activate'})
    Modal.confirm({
      title: 'The jackpot will be deactivated, continue?',
      onOk: () => changeStatus({id, status: 'Deactivate' }),
      cancelText: 'Close',
    })
  }

  const changeStatus = async (params: {id: string, status: 'Activate' | 'Deactivate'}) => {
    loading.addLoading()
    await changeJackpotStatusById(params)
    onRefresh()
    loading.removeLoading()
  }

  return (
    <div className="jackpots">
      <FloatingButton
        text="Add New Jackpot"
        onClick={() => history("/jackpots/new")}
        disabled={!canEdit}
      />
      <ActionArea buttonText={'Search'} buttonAction={() => onRefresh()}>
        <SelectInput
          label="Status"
          value={params.status}
          onSelectChange={(e) => setParams({...params, status: e?.value ? e.value : e})}
          options={[
            {
              name: 'All',
              value: jackpotsStatusFilters.all
            },
            {
              name: 'Active',
              value: jackpotsStatusFilters.active
            },
            {
              name: 'Inactive',
              value: jackpotsStatusFilters.inactive
            },
          ]}
        />
      </ActionArea>
      <List
        rows={jackpots}
        onChange={(selectedRowKeys, selectedRows, order) => onChangeOrder(order)}
        columns={[
          {
            title: 'Template ID',
            dataIndex: 'templateId',
            key: 'templateId',
            render: (value: string) => parseTemplateId(value),
            sorter: true,
            width: 100
          },
          {
            title: 'Games',
            dataIndex: 'games',
            key: 'games',
            render: (value: string) => value,
            sorter: true,
            width: 300
          },
          {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (value: string) => value,
            sorter: true,
            width: 150
          },
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (value: string) => value,
            sorter: true,
            width: 150
          },
          {
            title: 'PCT',
            dataIndex: 'pct',
            key: 'pct',
            render: (value: string) => value,
            sorter: true,
            width: 100
          },
          {
            title: 'House Seed',
            dataIndex: 'houseSeed',
            key: 'houseSeed',
            render: (value: string) => value,
            sorter: true,
            width: 100
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (value: string) => <Tag color={value.toUpperCase() === 'ACTIVE' ? 'green' : 'red'}> {value} </Tag>,
            sorter: true,
            width: 100
          },
          {
            title: 'House Contrib',
            dataIndex: 'houseContrib',
            key: 'houseContrib',
            render: (value: string) => value,
            sorter: true,
            width: 150
          },
          {
            title: 'Player Contrib',
            dataIndex: 'playerContrib',
            key: 'playerContrib',
            render: (value: string) => value,
            sorter: true,
            width: 150
          },
          {
            title: 'Addition',
            dataIndex: 'addition',
            key: 'addition',
            render: (value: string) => value,
            sorter: true,
            width: 150
          },
          {
            title: 'Partial Prize',
            dataIndex: 'partialPrize',
            key: 'partialPrize',
            render: (value: string) => value,
            sorter: true,
            width: 150
          },
          {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            render: (value: string) => value,
            sorter: true,
            width: 150
          },
          {
            title: 'Activate',
            dataIndex: '',
            key: 'activate',
            width: 150,
            render: (value: IJackPot) => (
            <Button
              disabled={!canEdit}
              onClick={() => changeJackpotStatus(value.templateId, value.status)}
              type='primary'
              style={{ backgroundColor: value.status.toUpperCase() === 'ACTIVE' ? '#bd4247': '#669533', width: 91}}
            >
              <p>{value.status.toUpperCase() === 'ACTIVE' ? 'Deactivate' : 'Activate'}</p>
            </Button>
            ),
          },
          {
            title: 'Edit',
            dataIndex: '',
            key: 'edit',
            width: 100,
            render: (value: IJackPot) => (
            <Button
              disabled={!canEdit}
              onClick={() => history(`/jackpots/new/${value.templateId}`)}
              type='primary'
              style={{ backgroundColor: '#2fa4e7' }}
            >
              <EditOutlined style={{ fontSize: '16px', color: '#FFF' }} />
            </Button>
            ),
          },
        ]}
      />

    </div>
  )
}

export default index